import { subject } from '@casl/ability'
import {
  FollowUpActionStatus,
  type IncidentReport,
  IncidentReportCase,
  type IncidentReportCloseBody,
  type IncidentReportWithCase,
  UserRole
} from '@lmc/ts-common'
import { Button, Heading, TextArea, theme, Warning } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form, Modal } from 'antd'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import styled from 'styled-components'
import {
  ConfirmationModalContent,
  ConfirmationModalTitleContainer,
  ModalFooter
} from '../commonStyledComponents'

type CloseIncidentModalProps = {
  open: boolean
  setIsClosingIncident: (value: boolean) => void
  onSuccess?: () => void
  incidentReport: IncidentReportWithCase | IncidentReport
  saveChanges?: () => void
}
const CloseIncidentModal = ({
  open,
  setIsClosingIncident,
  incidentReport,
  onSuccess,
  saveChanges
}: CloseIncidentModalProps) => {
  const { id: incidentReportId, case: incidentCase } = incidentReport
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const updateFunction = (values: IncidentReportCloseBody) =>
    postJSON({
      url: `list/incident-report/${incidentReportId}/close`,
      method: 'PUT',
      json: values
    })

  const { mutateAsync: updateStatus, isLoading } = useMutation(
    ['incident-report', incidentReportId],
    updateFunction,
    {
      onSuccess: () => {
        showToast({
          title: t(incidentReportsConstants.INCIDENT_CLOSED)
        })
        setIsClosingIncident(false)
        queryClient.invalidateQueries(['incident-report'])
        queryClient.invalidateQueries(['incident-list'])
        onSuccess?.()
      }
    }
  )

  const onFinish = async (values: IncidentReportCloseBody) => {
    try {
      saveChanges?.()
      await updateStatus(values)
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
  }

  const nonCompletedActions =
    incidentCase?.followUpActions?.actions?.filter(
      (action) => action.status !== FollowUpActionStatus.completed
    ) || []
  const hasNonCompletedActions = nonCompletedActions.length > 0

  // CM-4059: closing an incident is a Delete-tier action on the backend
  // (closeIncidentReport requires Action.Delete on Incident), so gate on `delete`
  // — Senior Carers have update-but-not-delete and must not see an enabled Close.
  const hasRolePermissionToClose = useAbilityAccess([
    {
      i: 'delete',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  return (
    <Modal
      closable={false}
      width={546}
      open={open}
      footer={[
        <ModalFooter>
          <Button onClick={() => setIsClosingIncident(false)}>
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button
            onClick={form.submit}
            isLoading={isLoading}
            variant="primary"
            disabledByPermission={!hasRolePermissionToClose}
          >
            {t(incidentReportsConstants.CLOSE_INCIDENT)}
          </Button>
        </ModalFooter>
      ]}
    >
      <ConfirmationModalContent>
        <ConfirmationModalTitleContainer>
          <Warning color={theme.color.status.warning} size={24} />
          <Heading level={4}>
            {t(incidentReportsConstants.CLOSE_INCIDENT_MODAL_TITLE)}
          </Heading>
        </ConfirmationModalTitleContainer>
        <Divider />
        {hasNonCompletedActions && (
          <ActionsContainer>
            <Text>
              {t(incidentReportsConstants.FOLLOWING_ACTIONS_NOT_COMPLETED)}
            </Text>
            {nonCompletedActions.map((action) => (
              <Action>
                <Text weight="bold">• {action.title}</Text>
              </Action>
            ))}
          </ActionsContainer>
        )}
        <Text>
          {incidentCase
            ? t(
                incidentReportsConstants.CLOSE_INCIDENT_MODAL_DESCRIPTION_WITH_CASE
              )
            : t(incidentReportsConstants.CLOSE_INCIDENT_MODAL_DESCRIPTION)}
        </Text>
        <CommentContainer>
          <CommentTextContainer>
            <CommentText weight="bold">
              {t(incidentReportsConstants.ADD_A_COMMENT)}
            </CommentText>
            <AsteriskText weight="bold">*</AsteriskText>
          </CommentTextContainer>

          <Form<IncidentReportCloseBody>
            form={form}
            onFinish={onFinish}
            onFinishFailed={({ errorFields }) =>
              errorFields.map((errorField) =>
                errorField.errors.map((err) =>
                  showToast({ title: err, type: 'error' })
                )
              )
            }
          >
            <Form.Item
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t(
                    incidentReportsConstants.ADDITIONAL_COMMENT_REQUIRED
                  )
                }
              ]}
              name="closeComment"
            >
              <TextArea />
            </Form.Item>
          </Form>
        </CommentContainer>
        <Divider />
      </ConfirmationModalContent>
    </Modal>
  )
}

const CommentText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const AsteriskText = styled(Text)`
  color: ${({ theme }) => theme.color.status.danger};
`

const CommentTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CommentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Action = styled.div`
  margin-left: 24px;
`

export default CloseIncidentModal
